import type { ShowToastOptions } from "@/contexts/NotificationContext"
import type { ParsedFormResult, UpdateDtaRequest } from "@/generated/sdk"
import { formDomain } from "./reuploadForms"

/**
 * The subset of the DTA-update contract the reupload replace sends.
 */
type EdcReuploadPayload = Required<Pick<UpdateDtaRequest, "document_content" | "domains">>

/**
 * Returns null when the replace can't run: no dtaId, no matching form, or form has no content.
 */
export function buildEdcReuploadPayload(
  forms: ParsedFormResult[],
  selectedName: string,
  dtaId: string | undefined,
): EdcReuploadPayload | null {
  if (!dtaId) return null
  const form = forms.find((f) => f.name === selectedName)
  if (!form?.document_content) return null
  return {
    document_content: form.document_content,
    domains: [formDomain(form)],
  }
}

interface EdcReuploadDeps {
  forms: ParsedFormResult[]
  selectedName: string
  dtaId: string | undefined
  updateContent: (payload: EdcReuploadPayload) => Promise<unknown>
  setSaving: (saving: boolean) => void
  clearForms: () => void
  showToast: (toast: ShowToastOptions) => void
}

/**
 * Deps are injected (not read from hooks) so the action is unit-testable without a render.
 */
export async function handleEdcReuploadReplace(deps: EdcReuploadDeps): Promise<void> {
  const payload = buildEdcReuploadPayload(deps.forms, deps.selectedName, deps.dtaId)
  if (!payload) return
  deps.setSaving(true)
  try {
    await deps.updateContent(payload)
    deps.clearForms()
  } catch (err: unknown) {
    const message = err instanceof Error ? err.message : String(err)
    deps.showToast({ subject: "Replace content failed", message, variant: "error" })
  } finally {
    deps.setSaving(false)
  }
}
